import { BaseToolbarProps } from './BaseToolbar.types';
import { styles } from './BaseToolbar.styles';
import React, { forwardRef, Fragment, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useBaseToolbarLogic } from './BaseToolbar.logic';
import Animated, { useAnimatedStyle, withDelay, withTiming } from 'react-native-reanimated';
import { mergeStyles } from 'src/_shared/utils/mergeStyles';
import { ToolbarTile } from './toolbar-tile/ToolbarTile';
import { ToolbarOptions } from './toolbar-options/ToolbarOptions';
import { useOnClickOutside } from 'src/hooks/useOnClickOutside';
import { LayoutChangeEvent, useWindowDimensions, View } from 'react-native';
import { LookupCodes } from 'src/constants/lookup.constants';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { configurationTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { ToolbarButton } from './toolbar-tile/ToolbarButton';

export const BaseToolbar = observer(
  forwardRef((props: BaseToolbarProps, ref?: any) => {
    const { isV3, maxWidth: widthLimit = 270, maxHeight = 600 } = props;
    const BaseToolbarRef = ref || useRef(null);
    const {
      areTilesExpanded,
      isToolbarOptionsOpened,
      expandTiles,
      collapseTiles,
      tiles,
      options,
      isVisible,
      onClose,
      setTutorialArea,
      onDuplicate,
      onDelete,
    } = useBaseToolbarLogic(props);

    const { height: windowHeight } = useWindowDimensions();
    const topOffset = BaseToolbarRef.current?.getBoundingClientRect()?.top ?? 0;
    const containerHeight = Math.max(BaseToolbarRef.current?.getBoundingClientRect()?.height ?? 0, 300);
    const heightLimit = windowHeight > maxHeight + topOffset ? maxHeight : containerHeight;
    const [tilesWidth, setTilesWidth] = React.useState(widthLimit);

    const containerAnimatedStyle = useAnimatedStyle(() => {
      let animatedHeight = 0;
      let animatedWidth = 0;
      if (isToolbarOptionsOpened) {
        animatedWidth = widthLimit;
        animatedHeight = heightLimit;
      } else if (areTilesExpanded) {
        animatedWidth = isV3 ? 200 : 184;
      } else {
        animatedWidth = 55;
      }

      return {
        width: withTiming(animatedWidth, { duration: 500 }),
        minHeight: withTiming(animatedHeight, { duration: 500 }),
      };
    }, [areTilesExpanded, isToolbarOptionsOpened, heightLimit, widthLimit, tilesWidth]);

    const containerToolTilesAnimatedStyle = useAnimatedStyle(() => {
      return {
        opacity: withTiming(isToolbarOptionsOpened ? 0 : 1, { duration: 250 }),
      };
    }, [isToolbarOptionsOpened]);

    const containerToolOptionsAnimatedStyle = useAnimatedStyle(() => {
      return {
        opacity: isToolbarOptionsOpened
          ? withDelay(250, withTiming(1, { duration: 500 }))
          : withTiming(0, { duration: 250 }),
        zIndex: isToolbarOptionsOpened ? 1 : withDelay(250, withTiming(-1, { duration: 0 })),
      };
    }, [isToolbarOptionsOpened]);

    const visibilityAnimatedStyle = useAnimatedStyle(() => {
      return {
        opacity: isVisible ? withTiming(1, { duration: 500 }) : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
      };
    }, [isVisible]);

    const handleLayout = (event: LayoutChangeEvent) => {
      'worklet';
      const { width, height, top, left } = event.nativeEvent.layout;
      // setting highlight zone for tutorial
      setTutorialArea({
        key: 'toolbarArea',
        value: {
          width,
          height,
          x: left,
          y: top,
          borderRadius: 24,
        },
      });
    };
    useOnClickOutside(BaseToolbarRef, onClose);

    return (
      <Animated.View
        style={mergeStyles(styles.container, containerAnimatedStyle, visibilityAnimatedStyle, props.style)}
        ref={BaseToolbarRef}
        onMouseEnter={() => {
          expandTiles();
        }}
        onMouseLeave={() => {
          collapseTiles();
        }}
        onLayout={handleLayout}
      >
        {/* First level of the toolbar */}
        {tiles && tiles.length > 0 && (
          <Animated.View style={[styles.containerInner, styles.containerToolTiles, containerToolTilesAnimatedStyle]}>
            <View
              onLayout={e => {
                const width = e.nativeEvent.layout.width;
                setTilesWidth(width);
              }}
            >
              {tiles.map(props => (
                <Fragment key={`Tool-tile-${props.tool.code}-${props.tool.titleKey}`}>
                  <ToolbarTile isV3={isV3} {...props} />
                  {isV3 && props.tool.code === LookupCodes.FORMAT && (
                    <View style={styles.separator} pointerEvents="none" />
                  )}
                </Fragment>
              ))}
              {isV3 && (
                <>
                  {isV3 && <View style={styles.separator} pointerEvents="none" />}
                  <ToolbarButton
                    iconName={ICON_NAMES.Copy}
                    text={'group-action-bar-v3:duplicate-selected'}
                    testID={configurationTestID(TestIDs.DUPLICATE_SELECTED)}
                    focusId={configurationTestID(TestIDs.DUPLICATE_SELECTED)}
                    focusOrder={tiles.length + 1}
                    focusGroup={FocusGroups.TOOLBAR_TILES}
                    focusable={isVisible}
                    onPress={async () => onDuplicate()}
                  />
                  <ToolbarButton
                    iconName={ICON_NAMES.Delete}
                    text={'group-action-bar-v3:delete-selected'}
                    testID={configurationTestID(TestIDs.DELETE_SELECTED)}
                    focusId={configurationTestID(TestIDs.DELETE_SELECTED)}
                    focusOrder={tiles.length + 2}
                    focusGroup={FocusGroups.TOOLBAR_TILES}
                    focusable={isVisible}
                    onPress={async () => onDelete()}
                  />
                </>
              )}
            </View>
          </Animated.View>
        )}

        {/* Second level of the toolbar */}
        <Animated.View
          style={[
            styles.containerInner,
            styles.containerToolOptions,
            containerToolOptionsAnimatedStyle,
            {
              height: heightLimit,
              width: widthLimit,
            },
          ]}
        >
          {options.map(option => (
            <ToolbarOptions key={`Tool-options-${option.tool.code}-${option.tool.titleKey}`} {...option} />
          ))}
        </Animated.View>
      </Animated.View>
    );
  }),
);

BaseToolbar.displayName = 'BaseToolbar';
